import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './AuditResult.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';
import { toast } from 'react-toastify';
import { getApiUrl } from '../api';

/* ── Constants ───────────────────────────────────── */
const SLIDER_MIN     = 20;
const SLIDER_MAX     = 1000;
const SLIDER_DEFAULT = 150;

const GRADE_CONFIG = {
  A: { color: '#16a34a', shadow: 'rgba(22,163,74,0.4)',   fill: 95, headline: 'Outstanding Reputation',     desc: 'You respond to nearly every review. Customers feel heard and new visitors trust you immediately. You\'re in the top tier of your industry.' },
  B: { color: '#2563eb', shadow: 'rgba(37,99,235,0.4)',   fill: 72, headline: 'Solid Reputation',           desc: 'Good engagement with most reviewers. A little more consistency could push you into the top tier and meaningfully impact revenue.' },
  C: { color: '#ca8a04', shadow: 'rgba(202,138,4,0.4)',   fill: 50, headline: 'Room to Improve',            desc: 'You respond sometimes, but inconsistently. Customers who feel ignored tend to take their business — and their referrals — elsewhere.' },
  D: { color: '#ea580c', shadow: 'rgba(234,88,12,0.4)',   fill: 28, headline: 'Reputation at Risk',         desc: 'Most reviews go unanswered. This signals to potential customers that your business doesn\'t prioritise feedback — a costly perception.' },
  F: { color: '#dc2626', shadow: 'rgba(220,38,38,0.4)',   fill: 8,  headline: 'Critical — Action Required', desc: 'No engagement with customer reviews. Every unanswered review is a missed opportunity to recover trust and win repeat business.' },
};

const SCALE_PILLS = [
  { letter: 'A', color: '#16a34a' },
  { letter: 'B', color: '#2563eb' },
  { letter: 'C', color: '#ca8a04' },
  { letter: 'D', color: '#ea580c' },
  { letter: 'F', color: '#dc2626' },
];

/* ── Helpers ─────────────────────────────────────── */
function getGrade(responseRateStr, negCount, totalReviews) {
  const pct      = parseFloat(responseRateStr) || 0;
  const negRatio = totalReviews > 0 ? negCount / totalReviews : 0;
  const adjusted = pct - negRatio * 20;
  if (adjusted >= 70) return 'A';
  if (adjusted >= 45) return 'B';
  if (adjusted >= 25) return 'C';
  if (adjusted >= 5)  return 'D';
  return 'F';
}

function calcRevenue(txValue, negative, neutral, positive) {
  const negLost     = negative * 30 * txValue;
  const neutralLost = neutral  * 10 * txValue;
  const posLost     = positive * 5  * txValue;
  return { negLost, neutralLost, posLost, total: negLost + neutralLost + posLost };
}

function fmtMoney(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function today() {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const SUBSCRIPTION_LABELS = {
  active: 'Active subscription',
  past_due: 'Payment overdue',
  unpaid: 'Payment required',
  canceled: 'Subscription canceled',
  incomplete: 'Payment incomplete',
  incomplete_expired: 'Subscription expired',
  trialing: 'Trialing',
  paused: 'Paused',
  null: 'No active subscription',
};

const STATUS_COLORS = {
  active: '#047857',
  past_due: '#b45309',
  unpaid: '#831843',
  canceled: '#991b1b',
  incomplete: '#c2410c',
  incomplete_expired: '#6b21a8',
  trialing: '#4f46e5',
  paused: '#0f766e',
  default: '#475569',
};

/* ── Star row ─────────────────────────────────────── */
const StarRow = ({ rating }) => (
  <span className={styles.stars}>
    {[1,2,3,4,5].map((i) => (
      <svg key={i} width="13" height="13" viewBox="0 0 24 24"
        fill={i <= rating ? '#fbbf24' : 'none'}
        stroke={i <= rating ? '#fbbf24' : '#d1d5db'}
        strokeWidth="2" aria-hidden="true">
        <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/>
      </svg>
    ))}
  </span>
);

const tagClass = (t) => {
  if (t.includes('1') || t.includes('2') || t.toLowerCase().includes('negative')) return styles.tagNeg;
  if (t.includes('5') || t.toLowerCase().includes('positive'))                     return styles.tagPos;
  if (t.toLowerCase().includes('no response'))                                     return styles.tagWarn;
  return styles.tagGray;
};

/* ══════════════════════════════════════════════════
   SECTION LABEL
   ══════════════════════════════════════════════════ */
const SectionLabel = ({ num, title }) => (
  <div className={styles.sectionLabel}>
    <span className={styles.sectionNum}>{num}</span>
    <span className={styles.sectionTitle}>{title}</span>
    <span className={styles.sectionLine} />
  </div>
);

/* ══════════════════════════════════════════════════
   REVENUE SLIDER
   ══════════════════════════════════════════════════ */
function RevenueSlider({ negative, neutral, positive }) {
  const { t } = useLanguage();
  const [txVal, setTxVal]   = useState(SLIDER_DEFAULT);
  const [popKey, setPopKey] = useState(0);
  const prevRef             = useRef(SLIDER_DEFAULT);

  const rev     = calcRevenue(txVal, negative, neutral, positive);
  const fillPct = ((txVal - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;
  const bubbleLeft = `calc(${fillPct}% - ${fillPct * 0.2}px)`;

  const handleChange = useCallback((e) => {
    const v = Number(e.target.value);
    if (v !== prevRef.current) { setPopKey((k) => k + 1); prevRef.current = v; }
    setTxVal(v);
  }, []);

  return (
    <div className={styles.sliderCard}>
      <div className={styles.sliderTop}>
        <div className={styles.sliderTitleBlock}>
          <p className={styles.sliderTitle}>💸 {t('revenueRiskCalc', 'Revenue Risk Calculator')}</p>
          <p className={styles.sliderSubtitle}>{t('setAvgTxVal', 'Set your average transaction value to see the real dollar cost of ignored reviews.')}</p>
        </div>
        <div className={styles.sliderLiveWrap}>
          <p className={styles.sliderLiveLabel}>{t('estRevenueAtRisk', 'Est. Revenue at Risk')}</p>
          <p key={popKey} className={`${styles.sliderLiveAmt} ${styles.pop}`}>
            {fmtMoney(rev.total)}
          </p>
        </div>
      </div>

      <div className={styles.sliderWrap}>
        <div className={styles.sliderBubble} style={{ left: bubbleLeft }}>
          ${txVal}
        </div>
        <input
          id="revenue-slider"
          type="range"
          min={SLIDER_MIN} max={SLIDER_MAX} step={5}
          value={txVal}
          onChange={handleChange}
          className={styles.sliderInput}
          style={{ '--fill-pct': `${fillPct}%` }}
          aria-label="Average transaction value"
        />
      </div>
      <div className={styles.sliderTickRow}>
        {[SLIDER_MIN, 100, 250, 500, SLIDER_MAX].map((tVal) => (
          <span key={tVal} className={styles.sliderTick}>${tVal}</span>
        ))}
      </div>

      <div className={styles.breakdownTable}>
        <div className={styles.breakdownCell}>
          <span className={styles.breakdownEmoji}>😡</span>
          <p className={`${styles.breakdownAmt} ${styles.amtRed}`}>{fmtMoney(rev.negLost)}</p>
          <p className={styles.breakdownDesc}>{negative} {t('unansweredNegative', 'unanswered negative reviews')}<br/>(×30 {t('buyersLost', 'deterred customers')})</p>
        </div>
        <div className={styles.breakdownCell}>
          <span className={styles.breakdownEmoji}>😐</span>
          <p className={`${styles.breakdownAmt} ${styles.amtYellow}`}>{fmtMoney(rev.neutralLost)}</p>
          <p className={styles.breakdownDesc}>{neutral} {t('unansweredNeutral', 'unanswered neutral reviews')}<br/>(×10 {t('missedVisits', 'missed visits')})</p>
        </div>
        <div className={styles.breakdownCell}>
          <span className={styles.breakdownEmoji}>😊</span>
          <p className={`${styles.breakdownAmt} ${styles.amtPurple}`}>{fmtMoney(rev.posLost)}</p>
          <p className={styles.breakdownDesc}>{positive} {t('unansweredPositive', 'unanswered positive reviews')}<br/>(×5 {t('missedRepeatVisits', 'missed repeat visits')})</p>
        </div>
      </div>

      <p className={styles.sliderFootnote}>
        Sources: Convergys (30× negative), BrightLocal (5× positive), Womply 200K+ SMB study. &nbsp;·&nbsp; Avg. transaction: <strong>${txVal}</strong>
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════ */
/* ── SAMPLE FALLBACK (shown when no live ID is present) ──────────── */
const SAMPLE_AUDIT = {
  _id: null,
  business: 'GamicaCloud (GCIT)',
  address: 'Dhobi Ghat, Faisalabad, Pakistan',
  rating: '4.9',
  totalReviews: 167,
  responseRate: '0%',
  respondedText: '0 of 8',
  status: 'completed',
  counts: { negative: 2, neutral: 0, positive: 6 },
  estimatedRevenue: '$36,000',
  reviews: [
    {
      id: 'r1', initial: 'M', name: 'MzRia EightySeven', rating: 1,
      tags: ['1-star', 'No response'],
      text: 'They have taken $70 unexpectedly from my account and I have never used their services. I have emailed them and have not heard back.',
      timeAgo: '10 months ago',
      suggestedResponse: 'Dear MzRia,\n\nWe sincerely apologise for this distressing experience — an unexpected charge combined with no email reply is completely unacceptable. Please reach out to us directly so we can investigate and resolve this for you immediately.',
    },
    {
      id: 'r2', initial: 'S', name: 'Shaheryar HAFEEZ', rating: 5,
      tags: ['5-star', 'No response'],
      text: 'Excellent learning experience through the course. Recommend for AI trainings.',
      timeAgo: '2 weeks ago',
      suggestedResponse: "Thank you so much for the kind words, Shaheryar! We're thrilled you had an excellent learning experience and recommend our AI training. We look forward to supporting you on your continued journey with GamicaCloud.",
    },
  ],
};

function AuditResult() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate  = useNavigate();
  const pollRef   = useRef(null);

  const [audit,   setAudit]   = useState(SAMPLE_AUDIT);
  const [loading, setLoading] = useState(!!id);
  const [fetchErr, setFetchErr] = useState(null);

  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleContinueToOnboarding = () => {
    // pass the auditRequest id so onboarding can save preferences
    if (audit && audit._id) navigate('/onboarding', { state: { auditRequestId: audit._id } });
    else navigate('/onboarding');
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const targetId = id || audit._id;
    if (!targetId) {
      toast.error('Cannot email a report for a sample audit. Please search and select a real business first.');
      return;
    }
    if (!email) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setIsSending(true);
    try {
      const response = await fetch(getApiUrl('/api/audit/send-report-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auditRequestId: targetId,
          email,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Report emailed successfully!');
        setEmail('');
      } else {
        toast.error(data.message || 'Failed to send email.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // ── Fetch / poll helper ─────────────────────────────────────────
  const fetchAudit = useCallback(async (auditId) => {
    try {
      const res = await fetch(getApiUrl(`/api/audit/${auditId}`), { credentials: 'include' });
      if (!res.ok) {
        setFetchErr('Could not load this audit. Showing sample data.');
        setLoading(false);
        return;
      }
      const json = await res.json();
      if (json.auditRequest) {
        setAudit(json.auditRequest);
        setLoading(false);
        // Stop polling once the scraper has finished (or errored)
        if (['completed', 'failed'].includes(json.auditRequest.status)) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      }
    } catch (err) {
      setFetchErr('Network error. Showing sample data.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      // No ID in the URL — just show the sample / demo data
      setLoading(false);
      return;
    }

    // Initial fetch
    fetchAudit(id);

    // Poll every 5 s while the scraper may still be running
    pollRef.current = setInterval(() => {
      setAudit((prev) => {
        if (['completed', 'failed'].includes(prev.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        return prev;
      });
      fetchAudit(id);
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, fetchAudit]);


  const { negative, neutral, positive } = audit.counts;
  const totalUnanswered = negative + neutral + positive;
  const grade  = getGrade(audit.responseRate, negative, audit.totalReviews);
  const config = GRADE_CONFIG[grade];

  const billing = audit.billing || {};
  const subscriptionStatus = billing.subscriptionStatus || null;
  const subscriptionLabel = SUBSCRIPTION_LABELS[subscriptionStatus] || SUBSCRIPTION_LABELS.default;
  const statusColor = STATUS_COLORS[subscriptionStatus] || STATUS_COLORS.default;
  const planLabel = audit.onboarding?.plan
    ? `${audit.onboarding.plan.charAt(0).toUpperCase()}${audit.onboarding.plan.slice(1)}`
    : billing.providerProductKey
      ? 'Subscribed plan'
      : 'No plan selected';
  const renewalDate = billing.currentPeriodEnd
    ? new Date(billing.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const subscriptionHint = subscriptionStatus === 'active'
    ? `Renews on ${renewalDate || 'an upcoming date'}`
    : subscriptionStatus
      ? 'Your Paddle subscription status is shown here.'
      : 'No subscription has been started yet.';

  // Decide if the scraper is still working
  const isPending    = ['pending', 'processing'].includes(audit.status);
  const isFailed     = audit.status === 'failed';

  return (
    <div className={styles.page}>

      {/* ── Status banner (pending / processing / failed) ── */}
      {loading && (
        <div className={styles.statusBanner} role="status">
          <span className={styles.statusSpinner} aria-hidden="true" />
          Loading audit…
        </div>
      )}
      {!loading && isPending && (
        <div className={`${styles.statusBanner} ${styles.statusBannerProcessing}`} role="status">
          <span className={styles.statusSpinner} aria-hidden="true" />
          Scraper is running — we're pulling your reviews from Google. This page refreshes automatically.
        </div>
      )}
      {!loading && isFailed && (
        <div className={`${styles.statusBanner} ${styles.statusBannerFailed}`} role="alert">
          ⚠️ The scraper encountered an error. The results below may be incomplete.
        </div>
      )}
      {!loading && fetchErr && (
        <div className={`${styles.statusBanner} ${styles.statusBannerFailed}`} role="alert">
          ⚠️ {fetchErr}
        </div>
      )}


      {/* ── Navbar ─────────────────────────────────── */}
      <Navbar className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<><BrandIcon size={22} />ReplyShoot</>}>
          <div className={styles.navLinks}>
            <Link to="/tools" className={styles.navLink}>{t('tools', 'Tools')}</Link>
            <LanguageToggle />
            <Link to="/audit" className={styles.navBtn}>{t('freeAudit', 'Free Audit')}</Link>
          </div>
      </Navbar>

      {/* ── DARK REPORT HEADER ─────────────────────── */}
      <div className={styles.reportHeader}>
        <div className={styles.reportHeaderInner}>

          {/* Meta row */}
          <div className={styles.reportMeta}>
            <span className={styles.reportBadge}>{t('reputationAuditReport', 'Reputation Audit Report')}</span>
            <span className={styles.reportDate}>{t('generatedOn', 'Generated')} {today()}</span>
          </div>

          {/* Business info + grade badge */}
          <div className={styles.reportTop}>
            <div className={styles.reportBizInfo}>
              <h1 className={styles.reportBizName}>{audit.business}</h1>
              <p className={styles.reportBizAddress}>{audit.address}</p>
              <div className={styles.reportBizMeta}>
                <span className={styles.reportBizRating}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fbbf24" stroke="#fbbf24" strokeWidth="0" aria-hidden="true">
                    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/>
                  </svg>
                  {audit.rating}
                </span>
                <span className={styles.reportBizDivider}>·</span>
                <span className={styles.reportBizReviews}>{audit.totalReviews} {t('totalReviewsOnGoogle', 'total reviews on Google')}</span>
              </div>
            </div>

            {/* Grade badge */}
            <div className={styles.reportGradeWrap}>
              <div
                className={styles.reportGradeBadge}
                style={{ '--grade-color': config.color, '--grade-shadow': config.shadow }}
              >
                {grade}
              </div>
              <span className={styles.reportGradeLabel}>{t('reputationScore', 'Reputation Score')}</span>
            </div>
          </div>

          {/* Metrics strip */}
          <div className={styles.reportMetricsStrip}>
            <div className={styles.reportMetricCell}>
              <p className={`${styles.reportMetricVal} ${styles.reportMetricValRed}`}>{audit.responseRate}</p>
              <p className={styles.reportMetricLabel}>{t('responseRateLabel', 'Response rate')}</p>
            </div>
            <div className={styles.reportMetricCell}>
              <p className={`${styles.reportMetricVal} ${styles.reportMetricValRed}`}>{totalUnanswered}</p>
              <p className={styles.reportMetricLabel}>{t('unansweredReviewsLabel', 'Unanswered reviews')}</p>
            </div>
            <div className={styles.reportMetricCell}>
              <p className={`${styles.reportMetricVal} ${styles.reportMetricValPurple}`}>{negative}</p>
              <p className={styles.reportMetricLabel}>{t('negativeHighestPriority', 'Negative — highest priority')}</p>
            </div>
            <div className={styles.reportMetricCell}>
              <p className={`${styles.reportMetricVal} ${styles.reportMetricValGreen}`}>{audit.estimatedRevenue}</p>
              <p className={styles.reportMetricLabel}>{t('estRevenueAtRiskLabel', 'Est. revenue at risk')}</p>
            </div>
          </div>

          <div className={styles.billingBanner}>
            <div className={styles.billingSummary}>
              <span className={styles.billingBadge} style={{ background: `${statusColor}22`, color: statusColor }}>
                {subscriptionLabel}
              </span>
              <div>
                <p className={styles.billingTitle}>{t('subscriptionDetails', 'Subscription details')}</p>
                <p className={styles.billingValue}>{planLabel}</p>
              </div>
            </div>
            <div className={styles.billingMeta}>
              <p className={styles.billingNote}>{subscriptionHint}</p>
              {billing.providerSubscriptionId && (
                <p className={styles.billingNote}>Subscription ID: {billing.providerSubscriptionId}</p>
              )}
              {billing.providerCustomerEmail && (
                <p className={styles.billingNote}>Customer email: {billing.providerCustomerEmail}</p>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── MAIN CONTENT ───────────────────────────── */}
      <main className={styles.main}>

        {/* SECTION 01 — Response Health */}
        <section>
          <SectionLabel num="01" title={t('responseHealthOverview', 'Response Health Overview')} />
          <div className={styles.healthGrid}>
            {/* Rate card */}
            <div className={styles.rateCard}>
              <p className={styles.rateCardLabel}>{t('reviewResponseRate', 'Review Response Rate')}</p>
              <p className={styles.rateCardNumber}>{audit.responseRate}</p>
              <p className={styles.rateCardSub}>
                {t('respondedTo', 'You responded to')} <strong>{audit.respondedText}</strong> {t('recentReviewsText', 'recent reviews')}
              </p>
              <p className={styles.rateCardNote}>
                {t('respondingBenchmark', 'Businesses responding to 25%+ of reviews earn 35% more revenue on average (Womply, 200K+ SMBs)')}
              </p>
            </div>

            {/* Stacked stat rows */}
            <div className={styles.statStack}>
              <div className={`${styles.statRow} ${styles.statRowRed}`}>
                <div className={`${styles.statRowIcon} ${styles.statRowIconRed}`}>⚠️</div>
                <div className={styles.statRowBody}>
                  <p className={styles.statRowTitle}>{t('unansweredNegative', 'Unanswered negative reviews')}</p>
                  <p className={styles.statRowHint}>{t('negUrgency', '1–2 stars · Highest urgency')}</p>
                </div>
                <p className={`${styles.statRowNum} ${styles.statRowNumRed}`}>{negative}</p>
              </div>
              <div className={`${styles.statRow} ${styles.statRowYellow}`}>
                <div className={`${styles.statRowIcon} ${styles.statRowIconYellow}`}>⏱️</div>
                <div className={styles.statRowBody}>
                  <p className={styles.statRowTitle}>{t('unansweredNeutral', 'Unanswered neutral reviews')}</p>
                  <p className={styles.statRowHint}>{t('neuUrgency', '3 stars · Recoverable with a reply')}</p>
                </div>
                <p className={`${styles.statRowNum} ${styles.statRowNumYellow}`}>{neutral}</p>
              </div>
              <div className={`${styles.statRow} ${styles.statRowGreen}`}>
                <div className={`${styles.statRowIcon} ${styles.statRowIconGreen}`}>⭐</div>
                <div className={styles.statRowBody}>
                  <p className={styles.statRowTitle}>{t('unansweredPositive', 'Unanswered positive reviews')}</p>
                  <p className={styles.statRowHint}>{t('posUrgency', '4–5 stars · Missed loyalty opportunity')}</p>
                </div>
                <p className={`${styles.statRowNum} ${styles.statRowNumPurple}`}>{positive}</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 02 — Reputation Grade */}
        <section>
          <SectionLabel num="02" title={t('reputationScoreBreakdown', 'Reputation Score Breakdown')} />
          <div
            className={styles.gradeCard}
            style={{
              '--grade-color': config.color,
              '--grade-shadow': config.shadow,
              '--grade-letter': `"${grade}"`,
            }}
          >
            <div className={styles.gradeBigBadge}>{grade}</div>
            <div className={styles.gradeDetails}>
              <p className={styles.gradeEyebrow}>{t('reputationGrade', 'Reputation Grade')}</p>
              <p className={styles.gradeHeadline}>{config.headline}</p>
              <p className={styles.gradeDesc}>{config.desc}</p>
              <div className={styles.gradeMeterRow}>
                <div className={styles.gradeMeterTrack}>
                  <div className={styles.gradeMeterFill} style={{ width: `${config.fill}%` }} />
                </div>
                <span className={styles.gradeMeterVal}>{config.fill} / 100</span>
              </div>
              <div className={styles.gradeScaleRow}>
                {SCALE_PILLS.map((p) => (
                  <span key={p.letter}
                    className={`${styles.gradeScalePill} ${p.letter === grade ? styles.gradeScalePillActive : ''}`}
                    style={{ color: p.color }}
                  >
                    {p.letter}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 03 — Revenue Impact */}
        <section>
          <SectionLabel num="03" title={t('revenueImpactAnalysis', 'Revenue Impact Analysis')} />
          <RevenueSlider negative={negative} neutral={neutral} positive={positive} />
        </section>

        {/* TEASER CTA — above AI Replies */}
        <div className={styles.teaserCta}>
          <div className={styles.teaserCtaGlow} aria-hidden="true" />
          <div className={styles.teaserCtaBody}>
            <span className={styles.teaserCtaBadge}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
              {t('trustedBy', 'Built for local businesses, big and small')}
            </span>
            <p className={styles.teaserCtaTitle}>
              {t('stopLosingCustomers', 'Reply to every review, and keep more customers.')}
              <span className={styles.teaserCtaAccent}>{t('letUsReplyForYou', ' Let us reply for you — starting at $15/mo.')}</span>
            </p>
            <p className={styles.teaserCtaSub}>
              {t('teaserCtaSubText', 'Our AI drafts a personalized, brand-aligned reply to every review, usually within 24 hours. You review and approve — we do the rest. Zero contracts, cancel any time.')}
            </p>
            <div className={styles.teaserCtaFeatures}>
              <span><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>{t('humanWrittenFeature', 'Personalized to each review, never a generic template')}</span>
              <span><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>{t('youApproveFeature', 'You approve every reply before it posts')}</span>
              <span><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>{t('first7DaysFreeFeature', 'Cancel anytime, no long-term contract')}</span>
            </div>
          </div>
          <div className={styles.teaserCtaActions}>
            <button type="button" className={styles.teaserCtaBtn} onClick={handleContinueToOnboarding}>
              {t('startNow', 'Start Now!')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </button>
            <p className={styles.teaserCtaNote}>{t('setupTimeNote', 'Setup takes under 15 minutes · Instant access')}</p>
          </div>
        </div>

        {/* SECTION 04 — AI Replies */}
        <section>
          <SectionLabel num="04" title={t('aiSuggestedResponses', 'AI-Suggested Responses')} />
          <div className={styles.repliesIntro}>
            <div className={styles.repliesIntroText}>
              <p className={styles.repliesIntroHeadline}>{t('heresWhatWeWouldSay', "Here's what we'd say for you")}</p>
              <p className={styles.repliesIntroSub}>{t('professionalBrandAligned', 'Professional, brand-aligned responses crafted for your unanswered reviews — ready in seconds.')}</p>
            </div>
            <span className={styles.repliesCount}>{Math.min(audit.reviews.length, 10)} {t('sampleResponses', 'sample responses')}</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20 }}>
            {audit.reviews.slice(0, 10).map((r) => (
              <div key={r.id} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewerRow}>
                    <div className={styles.reviewerLeft}>
                      <div className={styles.reviewerAvatar}>{r.initial}</div>
                      <div>
                        <p className={styles.reviewerName}>{r.name}</p>
                        <StarRow rating={r.rating} />
                      </div>
                    </div>
                    <div className={styles.tagRow}>
                      {r.tags.map((t, i) => (
                        <span key={i} className={`${styles.tag} ${tagClass(t)}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <p className={styles.reviewText}>{r.text}</p>
                  <p className={styles.reviewTime}>{r.timeAgo}</p>
                </div>
                {r.suggestedResponse && (
                  <div className={styles.replyBlock}>
                    <div className={styles.replyHeader}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9219a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"/>
                      </svg>
                      <span className={styles.replyLabel}>{t('replyShootSuggestedResponse', 'ReplyShoot Suggested Response')}</span>
                      <span className={styles.replyAiBadge}>{t('aiDrafted', 'AI-Drafted')}</span>
                    </div>
                    <p className={styles.replyText}>{r.suggestedResponse}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* SECTION 05 — Get Started */}
        <section>
          <SectionLabel num="05" title={t('nextSteps', 'Next Steps')} />
          <div className={styles.bottomGrid}>
            {/* Email capture */}
            <div className={styles.emailCard}>
              <p className={styles.emailCardTitle}>📬 {t('emailThisReport', 'Email this report')}</p>
              <p className={styles.emailCardSub}>
                {t('receivePdfCopy', 'Receive a PDF copy of this audit plus a free monthly check-in on how your reputation is trending.')}
              </p>
              <form className={styles.emailForm} onSubmit={handleEmailSubmit}>
                <input
                  id="audit-result-email"
                  required type="email"
                  placeholder="you@yourbusiness.com"
                  className={styles.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSending}
                />
                <button type="submit" className={styles.emailBtn} disabled={isSending}>
                  {isSending ? t('sendingBtn', 'Sending...') : t('sendReportBtn', 'Send Report')}
                </button>
              </form>
            </div>

            {/* CTA */}
            <div className={styles.ctaCard}>
              <div className={styles.ctaCardInner}>
                <div className={styles.ctaCardIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
                  </svg>
                </div>
                <p className={styles.ctaCardTitle}>{t('letUsHandleThisTitle', 'Let us handle this for you')}</p>
                <p className={styles.ctaCardDesc}>
                  {t('letUsHandleThisDesc', 'We respond to every review — positive and negative — in your brand voice, within 24 hours. You simply approve via email.')}
                </p>
                <p className={styles.ctaCardMeta}>
                  {t('startingAt15Mo', 'Starting at $15/month · Cancel anytime · No contracts')}
                </p>
                <button type="button" className={styles.ctaCardBtn} onClick={handleContinueToOnboarding}>
                  {t('getStarted', 'Get Started')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Re-run */}
        <div className={styles.rerunWrap}>
          <button className={styles.rerunBtn} onClick={() => navigate('/audit')}>
            ← {t('runAnotherAudit', 'Run another audit')}
          </button>
        </div>

      </main>

      <Footer />
      
    </div>
  );
}

export default AuditResult;
